import { useMemo } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FaPlus } from "react-icons/fa";
import { Box } from "../../components/Box";
import { Flex } from "../../components/Flex";
import { H2 } from "../../components/Heading";
import { Paragraph } from "../../components/Paragraph";
import { Button } from "../../components/Button";
import { Link } from "../../components/Link";
import { Loader } from "../../components/Loader";
import { fadeIn } from "../../components/animation";
import { useSplashScreens } from "../../data/useSplashScreen";
import { useDivisions } from "../../data/useDivisions";
import { SplashScreenList } from "../../types/SplashScreenList";
import { ROUTE } from "../../routes";
import { env } from "../../utils/env";

// auto-fill (not auto-fit) so a lone card keeps its column width instead of
// stretching across the row.
const Grid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  ${({ theme: { space } }) => `
    grid-gap: ${space[3]};
    margin-top: ${space[3]};
  `}
`;

const Card = styled(RouterLink)`
  animation: fadein 0.25s;
  ${fadeIn}

  ${({ theme: { colors, radii, space } }) => `
    display: flex;
    flex-direction: column;
    gap: ${space[2]};
    min-width: 0;
    text-decoration: none;
    color: inherit;
    background: ${colors.gray[10]};
    border-radius: ${radii[1]};
    padding: ${space[2]};
    transition: all 0.2s ease 0s;
    border: 1px solid ${colors.gray[100]};

    &:hover {
      transform: scale(1.010, 1.010);
      box-shadow: 0px 5px 9px rgb(0 0 0 / 8%);
    }

    &:active {
      transform: scale(1, 1);
    }
  `}
`;

/** Grey stage the phone mock-up sits on. */
const Stage = styled.div`
  ${({ theme: { colors, radii, space } }) => `
    display: flex;
    justify-content: center;
    padding: ${space[2]} 0 0;
    background: ${colors.gray[100]};
    border-radius: ${radii[1]};
    overflow: hidden;
    height: 150px;
  `}
`;

/** Top of a phone, cropped by the stage, in the splash screen's colours. */
const Phone = styled.div<{ background: string }>`
  ${({ theme: { colors, space, fontFamily }, background }) => `
    width: 140px;
    height: 170px;
    border-radius: 18px;
    border: 4px solid ${colors.gray[80]};
    background: ${background || colors.white};
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${space[1]};
    padding: ${space[2]} ${space[1]};
    font-family: ${fontFamily};
    text-align: center;
  `}
`;

const PhoneText = styled.div<{ color: string }>`
  ${({ theme: { colors }, color }) => `
    font-size: 9px;
    line-height: 1.3;
    color: ${color || colors.darkBlue};
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const PhoneButton = styled.div<{ background: string; color: string }>`
  ${({ theme: { colors, radii }, background, color }) => `
    width: 100%;
    padding: 3px 0;
    border-radius: ${radii[0]};
    font-size: 9px;
    font-weight: 600;
    background: ${background || colors.blue.primary};
    color: ${color || colors.white};
  `}
`;

const PhoneLink = styled.div<{ color: string }>`
  ${({ theme: { colors }, color }) => `
    font-size: 8px;
    text-decoration: underline;
    color: ${color || colors.blue.primary};
  `}
`;

const Clamp = styled(Paragraph)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusPill = styled.span<{ isOn: boolean }>`
  ${({ theme: { colors, radii, space, fontSizes, fontFamily }, isOn }) => `
    display: inline-flex;
    align-items: center;
    gap: ${space[0]};
    padding: 2px ${space[1]};
    border-radius: ${radii[0]};
    font-family: ${fontFamily};
    font-size: ${fontSizes[0]};
    font-weight: 500;
    background: ${isOn ? colors.green[50] : colors.gray[100]};
    color: ${isOn ? colors.gray[80] : colors.gray[60]};

    &::before {
      content: "";
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${isOn ? colors.green.primary : colors.gray[40]};
    }
  `}
`;

const EditLink = styled.span`
  ${({ theme: { colors, fontSizes, fontFamily } }) => `
    font-family: ${fontFamily};
    font-size: ${fontSizes[1]};
    font-weight: 500;
    color: ${colors.blue.primary};
  `}
`;

const divisionIdsOf = (s: SplashScreenList) =>
  (s.divisions ?? []).map((d) => (typeof d === "number" ? d : d.id));

const SettingsSplashScreens = () => {
  const navigate = useNavigate();

  const { data: divisionsQuery } = useDivisions();
  const divisionName = useMemo(
    () =>
      new Map(
        (divisionsQuery?.data ?? []).map((d) => [d.id, d.display_name]),
      ),
    [divisionsQuery?.data],
  );

  const { data: splashScreens = [], isLoading } = useSplashScreens();

  return (
    <Box bg="white" borderRadius="16px" p={5} overflow="hidden">
      <Flex justifyContent="space-between" alignItems="center">
        <H2 fontSize="3">Mobile app splash screens</H2>
        <Button
          Icon={FaPlus}
          iconSize="16px"
          onClick={() => navigate(ROUTE.SETTINGS_BRANDING_SPLASH_NEW)}
        >
          New Splash Screen
        </Button>
      </Flex>

      <Paragraph fontWeight="500" fontSize={1} color="gray.60" mt="0" mb={3}>
        Add your branding, terms and company information to the Verify mobile
        app, per division.
        <Link
          fontSize="1"
          href={env.verifyApplicationSplashScreenLearnMoreUrl}
          target="_blank"
          ml={2}
        >
          Learn more
        </Link>
      </Paragraph>

      <Paragraph m="0" fontSize={1} color="gray.60">
        {!isLoading &&
          `${splashScreens.length} splash screen${
            splashScreens.length === 1 ? "" : "s"
          }`}
      </Paragraph>

      {isLoading ? (
        <Flex justifyContent="center" alignItems="center" minHeight="25vh">
          <Loader />
        </Flex>
      ) : splashScreens.length === 0 ? (
        <Paragraph color="gray.60" mt={3}>
          No splash screens found.
        </Paragraph>
      ) : (
        <Grid>
          {splashScreens.map((s) => {
            const names = divisionIdsOf(s).map(
              (id) => divisionName.get(id) ?? `#${id}`,
            );
            return (
              <Card
                key={s.id}
                to={ROUTE.SETTINGS_BRANDING_SPLASH.replace(":id", String(s.id))}
              >
                <Stage>
                  <Phone background={s.background}>
                    <PhoneText color={s.font}>
                      {s.description || "Welcome"}
                    </PhoneText>
                    <PhoneButton
                      background={s.button_card_display}
                      color={s.button_font_color}
                    >
                      Continue
                    </PhoneButton>
                    {s.display_terms && (
                      <PhoneLink color={s.link_font_color}>
                        Terms &amp; Privacy
                      </PhoneLink>
                    )}
                  </Phone>
                </Stage>
                <Box minWidth={0}>
                  <Clamp
                    m="0"
                    fontSize={2}
                    fontWeight={600}
                    title={names.join(", ")}
                  >
                    {names.length ? names.join(", ") : "No divisions"}
                  </Clamp>
                  <Clamp
                    m="0"
                    mt={0}
                    fontSize={1}
                    color="gray.60"
                    title={s.description}
                  >
                    {s.description || "No welcome message"}
                  </Clamp>
                </Box>
                <Flex justifyContent="space-between" alignItems="center">
                  <StatusPill isOn={s.display_splash_screen}>
                    {s.display_splash_screen ? "Displayed" : "Hidden"}
                  </StatusPill>
                  <EditLink>Edit &rarr;</EditLink>
                </Flex>
              </Card>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default SettingsSplashScreens;
